<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="sm:flex sm:items-center sm:justify-between">
      <div>
        <div class="flex items-center space-x-3">
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">{{ plan.name }}</h1>
          <span
            :class="[
              plan.is_active
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
              'px-2 py-1 text-xs font-semibold rounded-full'
            ]"
          >
            {{ plan.is_active ? 'Active' : 'Inactive' }}
          </span>
          <span
            v-if="plan.is_public"
            class="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
          >
            Public
          </span>
        </div>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {{ plan.slug }}
        </p>
      </div>
      <div class="mt-4 flex space-x-3 sm:mt-0">
        <Link
          :href="route('site-owner.subscriptions.plans.features.index', plan.id)"
          class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Cog6ToothIcon class="-ml-1 mr-2 h-5 w-5" />
          Manage Features
        </Link>
        <Link
          :href="route('site-owner.subscriptions.plans.edit', plan.id)"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PencilIcon class="-ml-1 mr-2 h-5 w-5" />
          Edit Plan
        </Link>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <!-- Active Subscriptions -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <UsersIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  Active Subscribers
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  {{ stats.active_subscriptions }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Trial Subscriptions -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <ClockIcon class="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  On Trial
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  {{ stats.trial_subscriptions }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Monthly Revenue -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <BanknotesIcon class="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  Monthly Revenue
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  £{{ stats.monthly_revenue.toFixed(2) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Annual Revenue -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <CurrencyPoundIcon class="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  Annual Revenue
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  £{{ stats.annual_revenue.toFixed(2) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Plan Details & Features Grid -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- Plan Details -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Plan Details
          </h3>
          <dl class="space-y-3">
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Price</dt>
              <dd class="text-sm text-gray-900 dark:text-white font-semibold">
                {{ plan.is_free ? 'FREE' : `£${plan.price_monthly}` }}
              </dd>
            </div>
            <div v-if="plan.price_annual" class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Annual Price</dt>
              <dd class="text-sm text-gray-900 dark:text-white font-semibold">
                £{{ plan.price_annual }}
                <span v-if="plan.annual_savings_percentage" class="ml-1 text-xs text-green-600 dark:text-green-400">
                  (Save {{ plan.annual_savings_percentage }}%)
                </span>
              </dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Billing Period</dt>
              <dd class="text-sm text-gray-900 dark:text-white capitalize">{{ plan.billing_period }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Trial Period</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ plan.trial_days }} days</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Display Order</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ plan.sort_order }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Stripe Monthly Price ID</dt>
              <dd class="text-sm text-gray-900 dark:text-white font-mono">
                {{ plan.stripe_price_id_monthly || 'Not configured' }}
              </dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Stripe Annual Price ID</dt>
              <dd class="text-sm text-gray-900 dark:text-white font-mono">
                {{ plan.stripe_price_id_annual || 'Not configured' }}
              </dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ formatDate(plan.created_at) }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</dt>
              <dd class="text-sm text-gray-900 dark:text-white">{{ formatDate(plan.updated_at) }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Limits -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Plan Limits
            </h3>
            <span class="text-sm text-gray-500 dark:text-gray-400">
              {{ features.limits.length }} limits
            </span>
          </div>
          <div v-if="features.limits.length > 0" class="space-y-3">
            <div
              v-for="feature in features.limits"
              :key="feature.id"
              class="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
            >
              <span class="text-sm text-gray-700 dark:text-gray-300">{{ feature.feature_name }}</span>
              <span class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ formatFeatureValue(feature) }}
              </span>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500 dark:text-gray-400">
            No limits configured for this plan.
          </p>
        </div>
      </div>
    </div>

    <!-- Capabilities (Active Features) -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Plan Capabilities
          </h3>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{ activeCapabilities.length }} features
          </span>
        </div>
        <div v-if="activeCapabilities.length > 0" class="space-y-3">
          <div
            v-for="feature in activeCapabilities"
            :key="feature.id"
            class="flex items-start justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
          >
            <div class="flex items-center space-x-2">
              <CheckCircleIcon
                v-if="isFeatureEnabled(feature)"
                class="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0"
              />
              <XCircleIcon v-else class="h-5 w-5 text-gray-400 flex-shrink-0" />
              <span class="text-sm text-gray-700 dark:text-gray-300">{{ feature.feature_name }}</span>
            </div>
            <div class="text-right">
              <span v-if="feature.feature_key === 'modules_allowed'" class="text-sm text-gray-900 dark:text-white">
                {{ formatModulesAllowed(feature.feature_value) }}
              </span>
              <span v-else class="text-sm font-semibold" :class="isFeatureEnabled(feature) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'">
                {{ isFeatureEnabled(feature) ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">
          No capabilities configured for this plan.
        </p>
      </div>
    </div>

    <!-- Unused Feature Set - Future Integration -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Unused Feature Set - Future Integration
          </h3>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{ futureCapabilities.length }} features
          </span>
        </div>
        <div v-if="futureCapabilities.length > 0" class="space-y-3">
          <div
            v-for="feature in futureCapabilities"
            :key="feature.id"
            class="flex items-start justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
          >
            <div class="flex items-center space-x-2">
              <CheckCircleIcon
                v-if="isFeatureEnabled(feature)"
                class="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0"
              />
              <XCircleIcon v-else class="h-5 w-5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
              <span class="text-sm text-gray-500 dark:text-gray-400">{{ feature.feature_name }}</span>
            </div>
            <div class="text-right">
              <span class="text-sm font-semibold text-gray-400 dark:text-gray-500">
                {{ isFeatureEnabled(feature) ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">
          No future features configured.
        </p>
      </div>
    </div>

    <!-- Recent Subscribers -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
          Recent Subscribers
        </h3>
        <div v-if="subscribers.length > 0" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Company
                </th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Billing
                </th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Amount
                </th>
                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Current Period
                </th>
                <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="subscriber in subscribers" :key="subscriber.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td class="px-3 py-2 text-sm text-gray-900 dark:text-white">
                  {{ subscriber.company_name }}
                </td>
                <td class="px-3 py-2 text-sm">
                  <span
                    :class="[
                      getSubscriptionStatusColor(subscriber.status),
                      'px-2 py-1 text-xs font-semibold rounded-full'
                    ]"
                  >
                    {{ subscriber.status }}
                  </span>
                </td>
                <td class="px-3 py-2 text-sm text-gray-900 dark:text-white capitalize">
                  {{ subscriber.billing_period }}
                </td>
                <td class="px-3 py-2 text-sm text-gray-900 dark:text-white">
                  £{{ subscriber.amount }}
                </td>
                <td class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {{ subscriber.current_period_start }} - {{ subscriber.current_period_end }}
                </td>
                <td class="px-3 py-2 text-sm text-right">
                  <Link
                    :href="route('site-owner.companies.show', subscriber.company_id)"
                    class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    View Company
                  </Link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">
          No subscribers yet for this plan.
        </p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex items-center justify-between">
      <div class="flex space-x-3">
        <button
          @click="togglePlan"
          class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          {{ plan.is_active ? 'Deactivate Plan' : 'Activate Plan' }}
        </button>
        <button
          @click="duplicatePlan"
          class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <DocumentDuplicateIcon class="-ml-1 mr-2 h-5 w-5" />
          Duplicate Plan
        </button>
      </div>
      <button
        v-if="stats.active_subscriptions === 0"
        @click="deletePlan"
        class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
      >
        <TrashIcon class="-ml-1 mr-2 h-5 w-5" />
        Delete Plan
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import {
  PencilIcon,
  TrashIcon,
  UsersIcon,
  ClockIcon,
  BanknotesIcon,
  CurrencyPoundIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentDuplicateIcon,
  Cog6ToothIcon,
} from '@heroicons/vue/24/outline';
import route from '@/Utils/route';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  features: {
    type: Object,
    required: true,
  },
  stats: {
    type: Object,
    required: true,
  },
  subscribers: {
    type: Array,
    default: () => [],
  },
});

// Active capabilities that are currently in use
const activeCapabilityKeys = [
  'modules_allowed',
  'equipment_module',
  'assets_module',
  'document_upload',
  'sla_management',
];

// Split capabilities into active and future
const activeCapabilities = computed(() => {
  return props.features.capabilities.filter(feature =>
    activeCapabilityKeys.includes(feature.feature_key)
  );
});

const futureCapabilities = computed(() => {
  return props.features.capabilities.filter(feature =>
    !activeCapabilityKeys.includes(feature.feature_key)
  );
});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFeatureValue = (feature) => {
  if (feature.value_type === 'unlimited') {
    return 'Unlimited';
  }
  if (feature.value_type === 'boolean') {
    return feature.feature_value === 'true' ? 'Yes' : 'No';
  }
  return feature.feature_value;
};

const isFeatureEnabled = (feature) => {
  if (feature.value_type === 'boolean') {
    return feature.feature_value === 'true' || feature.feature_value === true;
  }
  return true;
};

const formatModulesAllowed = (value) => {
  try {
    // Parse the JSON array
    const modules = JSON.parse(value);

    if (!Array.isArray(modules) || modules.length === 0) {
      return 'None';
    }

    // Convert module keys to readable names
    const moduleNames = modules.map(module => {
      const names = {
        'it_helpdesk': 'IT Helpdesk',
        'maintenance': 'Maintenance',
        'incidents': 'Incidents',
        'assets': 'Assets',
        'equipment': 'Equipment',
        'projects': 'Projects',
        'time_tracking': 'Time Tracking',
        'reporting': 'Reporting',
      };
      return names[module] || module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    });

    return moduleNames.join(', ');
  } catch (e) {
    // If JSON parsing fails, return the raw value
    return value || 'None';
  }
};

const getSubscriptionStatusColor = (status) => {
  const colors = {
    trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    past_due: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    suspended: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  return colors[status] || colors.active;
};

const togglePlan = () => {
  if (confirm('Are you sure you want to change this plan\'s status?')) {
    router.post(route('site-owner.subscriptions.plans.toggle', props.plan.id), {}, {
      preserveScroll: true,
    });
  }
};

const duplicatePlan = () => {
  if (confirm('This will create a copy of the plan with all its features. Continue?')) {
    router.post(route('site-owner.subscriptions.plans.duplicate', props.plan.id));
  }
};

const deletePlan = () => {
  if (confirm(`Are you sure you want to delete the "${props.plan.name}" plan? This action cannot be undone.`)) {
    router.delete(route('site-owner.subscriptions.plans.destroy', props.plan.id));
  }
};
</script>
